import { FC } from 'react';
import { Settings } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { ToggleSwitch } from './ToggleSwitch';
import type { EditableProfileData } from './types';

export interface SettingsPanelProps {
  settings: EditableProfileData['settings'];
  onSettingsChange: (
    field: keyof EditableProfileData['settings'],
    value: boolean | number
  ) => void;
}

export const SettingsPanel: FC<SettingsPanelProps> = ({
  settings,
  onSettingsChange,
}) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-6 flex items-center gap-2">
        <Settings className="w-5 h-5" />
        Booking Settings
      </h3>
      <div className="space-y-6">
        <ToggleSwitch
          enabled={settings.autoAcceptBookings}
          onChange={(enabled) => onSettingsChange('autoAcceptBookings', enabled)}
          label="Auto-Accept Bookings"
          description="Automatically confirm new booking requests without manual approval"
        />

        <ToggleSwitch
          enabled={settings.emailNotifications}
          onChange={(enabled) => onSettingsChange('emailNotifications', enabled)}
          label="Email Notifications"
          description="Receive email alerts for new bookings, cancellations, and reminders"
        />

        <ToggleSwitch
          enabled={settings.smsNotifications}
          onChange={(enabled) => onSettingsChange('smsNotifications', enabled)}
          label="SMS Notifications"
          description="Receive text message alerts for important updates"
        />

        <div className="border-t border-gray-200 dark:border-slate-700 pt-6 space-y-4">
          <div>
            <label
              htmlFor="bufferTime"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
            >
              Buffer Time Between Appointments
            </label>
            <select
              id="bufferTime"
              value={settings.bufferTimeBetweenAppointments}
              onChange={(e) =>
                onSettingsChange('bufferTimeBetweenAppointments', Number(e.target.value))
              }
              className={cn(
                'w-full sm:w-48 px-3 py-2 text-sm rounded-md border',
                'bg-white dark:bg-slate-700',
                'text-gray-900 dark:text-slate-100',
                'border-gray-300 dark:border-slate-600',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              )}
            >
              <option value={0}>No buffer</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
              Time blocked between appointments for preparation
            </p>
          </div>

          <div>
            <label
              htmlFor="maxDaily"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
            >
              Max Daily Appointments
            </label>
            <input
              id="maxDaily"
              type="number"
              min={1}
              max={20}
              value={settings.maxDailyAppointments}
              onChange={(e) =>
                onSettingsChange('maxDailyAppointments', Number(e.target.value))
              }
              className={cn(
                'w-full sm:w-48 px-3 py-2 text-sm rounded-md border',
                'bg-white dark:bg-slate-700',
                'text-gray-900 dark:text-slate-100',
                'border-gray-300 dark:border-slate-600',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              )}
            />
            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
              Maximum number of appointments you can accept per day
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
